import { api, type Leaderboard } from '../api.js';
import { ce, el, clear, fmtMinutes, avatarEl } from '../dom.js';
import { t } from '../i18n.js';

export function renderBoard(): HTMLElement {
  const root = el('div', {});
  const error = el('p', { class: 'error' });
  const content = el('div', {});
  let period: 'week' | 'month' = 'week';
  let view: 'raw' | 'fairness' = 'raw';

  // A pair of two-way toggles. `@material/web` ships no segmented button, so
  // the selected side is a filled button and the other an outlined one — the
  // M3 way to show selection with fill and weight, not colour alone (ADR-0046).
  const toggle = (label: string, onClick: () => void): HTMLElement => {
    const node = ce('md-outlined-button', {}, label);
    node.addEventListener('click', onClick);
    return node;
  };
  const weekBtn = toggle(t('boardWeek'), () => setPeriod('week'));
  const monthBtn = toggle(t('boardMonth'), () => setPeriod('month'));
  const rawBtn = toggle(t('boardRaw'), () => setView('raw'));
  const fairBtn = toggle(t('boardFairness'), () => setView('fairness'));

  // Swapping the tag is how a Material button changes emphasis, so re-create
  // the element in place and carry the listener over.
  function setSelected(node: HTMLElement, selected: boolean, label: string, onClick: () => void): HTMLElement {
    const wanted = selected ? 'md-filled-button' : 'md-outlined-button';
    if (node.localName === wanted) return node;
    const next = ce(wanted, {}, label);
    next.addEventListener('click', onClick);
    node.replaceWith(next);
    return next;
  }

  const btns = { week: weekBtn, month: monthBtn, raw: rawBtn, fair: fairBtn };

  function setPeriod(p: 'week' | 'month'): void {
    period = p;
    load();
  }
  function setView(v: 'raw' | 'fairness'): void {
    view = v;
    load();
  }

  function load(): void {
    btns.week = setSelected(btns.week, period === 'week', t('boardWeek'), () => setPeriod('week'));
    btns.month = setSelected(btns.month, period === 'month', t('boardMonth'), () => setPeriod('month'));
    btns.raw = setSelected(btns.raw, view === 'raw', t('boardRaw'), () => setView('raw'));
    btns.fair = setSelected(btns.fair, view === 'fairness', t('boardFairness'), () => setView('fairness'));

    api
      .get<Leaderboard>(`/api/leaderboard?period=${period}`)
      .then((lb) => {
        clear(content);
        content.append(el('p', { class: 'muted' }, t('boardRange', { from: lb.from, to: lb.toExclusive })));
        for (const board of lb.boards) {
          const rows = [...board.rows].sort((a, b) =>
            view === 'raw' ? b.minutes - a.minutes : b.fairness - a.fairness,
          );
          const max = Math.max(...rows.map((r) => (view === 'raw' ? r.minutes : r.fairness)), 1e-9);
          const card = el('div', { class: 'card', 'data-board': board.board });
          card.append(el('h2', {}, board.board === 'family' ? t('boardFamily') : t('boardNamed', { name: board.board })));
          if (view === 'fairness') {
            card.append(
              el('p', { class: 'muted' }, t('boardFairnessHint')),
            );
          }
          for (const r of rows) {
            const value = view === 'raw' ? r.minutes : r.fairness;
            card.append(
              el(
                'div',
                { class: 'board-row' },
                avatarEl(r.name, r.avatar, r.color, 36),
                el('div', { style: 'width:80px;overflow:hidden;text-overflow:ellipsis' }, r.name),
                el('div', { class: 'bar-wrap' }, el('div', { class: 'bar', style: `width:${(value / max) * 100}%` })),
                el(
                  'div',
                  { class: 'value' },
                  view === 'raw' ? fmtMinutes(r.minutes) : `${(r.fairness * 100).toFixed(1)}%`,
                ),
              ),
            );
          }
          content.append(card);
        }
      })
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  }

  root.append(
    el('div', { class: 'row' }, weekBtn, monthBtn, el('span', { style: 'width:12px' }), rawBtn, fairBtn),
    error,
    content,
  );
  load();
  return root;
}
